import {Component, Input} from '@angular/core';
import {WindOnshoreSubTechnology, windSubTechnologyToLabel} from '../../../model/technical/WindOnshoreSubTechnology';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {UnitTypeEntry} from '../UnitTypeEntry';
import {AbstractUnits} from '../abstract-units';
import {UnitTypeWindOnshore} from '../../../model/technical/UnitTypeWindOnshore';
import {tsdMax, tsdMin, tsdMoreThan} from '../../../input/thousand-separator.directive';
import {CalcUtil} from '../../../CalcUtil';
import {ProjectDetailsTab} from '../../../../project-details/ProjectDetailsTab';
import {ReadOnlyReason, ReadOnlyReasonToTooltip} from '../../../../project-details/ReadOnlyStateManager';
import {FinancialModelReadManager} from '../../../../project-details/FinancialModelReadManager';

@Component({
  selector: 'digires-units-wind-onshore',
  templateUrl: './units-wind-onshore.component.html',
  styleUrls: ['./units-wind-onshore.component.scss']
})
export class UnitsWindOnshoreComponent extends AbstractUnits {

  public static createDefaultUnits(
    projectCreation: boolean,
    unitA?: UnitTypeWindOnshore,
    unitB?: UnitTypeWindOnshore,
    unitC?: UnitTypeWindOnshore,
    unitD?: UnitTypeWindOnshore,
  ): UnitTypeEntry[] {
    return UnitsWindOnshoreComponent.createDefaultUnitsInternal(
      projectCreation,
      unitA || UnitsWindOnshoreComponent.createDefaultUnit('windA'),
      unitB || UnitsWindOnshoreComponent.createDefaultUnit('windB'),
      unitC || UnitsWindOnshoreComponent.createDefaultUnit('windC'),
      unitD || UnitsWindOnshoreComponent.createDefaultUnit('windD'),
    );
  }

  private static createDefaultUnitsInternal(
    projectCreation: boolean,
    unitA: UnitTypeWindOnshore,
    unitB: UnitTypeWindOnshore,
    unitC: UnitTypeWindOnshore,
    unitD: UnitTypeWindOnshore,
  ): UnitTypeEntry[] {
    return [
      {
        id: 'windOnshoreUnitA',
        active: true,
        canAdd: false,
        canDelete: false,
        label: 'Wind Turbine Type A',
        fg: UnitsWindOnshoreComponent.createUnitFg(unitA, projectCreation),
      },
      {
        id: 'windOnshoreUnitB',
        active: unitB.active,
        canAdd: false,
        canDelete: false,
        label: 'Wind Turbine Type B',
        fg: UnitsWindOnshoreComponent.createUnitFg(unitB, projectCreation),
      },
      {
        id: 'windOnshoreUnitC',
        active: unitC.active,
        canAdd: false,
        canDelete: false,
        label: 'Wind Turbine Type C',
        fg: UnitsWindOnshoreComponent.createUnitFg(unitC, projectCreation),
      },
      {
        id: 'windOnshoreUnitD',
        active: unitD.active,
        canAdd: false,
        canDelete: false,
        label: 'Wind Turbine Type D',
        fg: UnitsWindOnshoreComponent.createUnitFg(unitD, projectCreation),
      },
    ];
  }

  private static createUnitFg(unit: UnitTypeWindOnshore, projectCreation: boolean): FormGroup {
    return new FormGroup({
      subTechnology: new FormControl(unit.subTechnology || WindOnshoreSubTechnology.GROUND_BASED),
      manufacturer: new FormControl(unit.manufacturer, [Validators.minLength(1)]),
      modelType: new FormControl(unit.modelType, [Validators.minLength(1)]),
      count: new FormControl(unit.count, [tsdMin(0), tsdMax(999)]),
      capacity: new FormControl(unit.capacity, [tsdMoreThan(0)]),
      totalCapacity: new FormControl(CalcUtil.calculateTotalCapacity(unit.count, unit.capacity, 1.0)),
      tipHeight: new FormControl(unit.tipHeight, [tsdMin(0)]),
      hubHeight: new FormControl(unit.hubHeight, [tsdMin(0)]),
      rotorDiameter: new FormControl(unit.rotorDiameter, [tsdMin(0)]),
      averageWindSpeed: new FormControl(unit.averageWindSpeed, [tsdMin(0)]),
      manufacturerByPermit: new FormControl(unit.manufacturerByPermit),
      modelTypeByPermit: new FormControl(unit.modelTypeByPermit),
      capacityMaxByPermit: new FormControl(unit.capacityMaxByPermit, [tsdMin(0)]),
      capacityMaxByPermitAmendment: new FormControl(unit.capacityMaxByPermitAmendment, [tsdMin(0)]),
      tipHeightMaxByPermit: new FormControl(unit.tipHeightMaxByPermit, [tsdMin(0)]),
      tipHeightMaxByPermitAmendment: new FormControl(unit.tipHeightMaxByPermitAmendment, [tsdMin(0)]),
      hubHeightMaxByPermit: new FormControl(unit.hubHeightMaxByPermit, [tsdMin(0)]),
      hubHeightMaxByPermitAmendment: new FormControl(unit.hubHeightMaxByPermitAmendment, [tsdMin(0)]),
      rotorDiameterMaxByPermit: new FormControl(unit.rotorDiameterMaxByPermit, [tsdMin(0)]),
      rotorDiameterMaxByPermitAmendment: new FormControl(unit.rotorDiameterMaxByPermitAmendment, [tsdMin(0)]),
      rotorGroundClearanceMinByPermit: new FormControl(unit.rotorGroundClearanceMinByPermit, [tsdMin(0)]),
    });
  }

  private static createDefaultUnit(id: string): UnitTypeWindOnshore {
    return {
      id: id,
      active: false,
      subTechnology: WindOnshoreSubTechnology.GROUND_BASED,
      manufacturer: null,
      modelType: null,
      count: null,
      capacity: null,
      tipHeight: null,
      hubHeight: null,
      rotorDiameter: null,
      averageWindSpeed: null,
      manufacturerByPermit: null,
      modelTypeByPermit: null,
      capacityMaxByPermit: null,
      capacityMaxByPermitAmendment: null,
      tipHeightMaxByPermit: null,
      tipHeightMaxByPermitAmendment: null,
      hubHeightMaxByPermit: null,
      hubHeightMaxByPermitAmendment: null,
      rotorDiameterMaxByPermit: null,
      rotorDiameterMaxByPermitAmendment: null,
      rotorGroundClearanceMinByPermit: null,
    };
  }

  readonly allSubTechnologies = Object.values(WindOnshoreSubTechnology);
  readonly calculatedReadOnlyReason = ReadOnlyReasonToTooltip[ReadOnlyReason.CALCULATED];

  constructor() {super(ProjectDetailsTab.windOnshore, 1.0);}

  subTechToLabel(value: WindOnshoreSubTechnology): string {
    return windSubTechnologyToLabel[value];
  }

}
